<script lang="ts">
  import FaTimes from 'svelte-icons/fa/FaTimes.svelte'
  import FaCheckCircle from 'svelte-icons/fa/FaCheckCircle.svelte'
  import FaTimesCircle from 'svelte-icons/fa/FaTimesCircle.svelte'
  import type { DataSourceInterface } from '../data-views/data-source-interface';
  import Loader from './loader.svelte';
  import type { Feedback } from '../data-views/types';
  import { t } from "../translations/i18n";

  export let visible = false;
  export let onClose: any;
  export let dataSource: DataSourceInterface;

  let feedback: Feedback;
  let showLoader: boolean = false;
  
  let showSuccessScreen: boolean = false;
  let showFailureScreen: boolean = false;
  let showEmptyFeedbackMessage: boolean = false;

  async function handleSubmit() {
    let inputName = (document.getElementById('input-name') as HTMLInputElement)?.value;
    let inputEmail = (document.getElementById('input-email') as HTMLInputElement)?.value;
    let feedbackContent = (document.getElementById('input-feedback') as HTMLInputElement)?.value;

    if (!feedbackContent) {
      showEmptyFeedbackMessage = true;
      return;
    }

    feedback = {
      name: inputName,
      email: inputEmail,
      feedbackText: feedbackContent
    };

    showLoader = true;
    const response_status = await dataSource.sendFeedback(feedback);
    showLoader = false;

    if (response_status === 200) {
      showSuccessScreen = true;
    } else {
      showFailureScreen = true;
    }
  }

  function toggleDrawer() {
    onClose();
  }
</script>

<!-- Overlay -->
<div class="overlay {visible ? 'visible' : ''}" on:click={toggleDrawer}/>

<!-- Drawer -->
<div class="drawer {visible ? 'open' : ''}">
  <div class="drawer-header">
    <h3>{$t("Feedback")}</h3>
    <button class="close-button" on:click={toggleDrawer}>
        <FaTimes />
    </button>
  </div>
  <div class="drawer-body">
    <Loader visible={showLoader} />
    {#if showSuccessScreen}
      <div class="result-screen success">
        <div class="result-screeen-icon success">
          <FaCheckCircle />
        </div>
        <p>{$t("Thank you for your feedback!")}</p>
      </div>
    {:else if showFailureScreen}
      <div class="result-screen failure">
        <div class="result-screeen-icon failure">
          <FaTimesCircle />
        </div>
        <p>{$t("Failed to send feedback. Please try again later.")}</p>
      </div>
    {:else}
    <div id="feedback-form">
      <p>{$t("Experienced a bug or just want to give a feedback? Please fill the form below so we can improve your experience!")}</p>
      <input id="input-name" type="text" placeholder={$t("Name (Optional)")} />
      <input id="input-email" type="text" placeholder={$t("Email (Optional)")} />
      <div class="feedback-field-wrapper">
        <textarea id="input-feedback" class="{showEmptyFeedbackMessage ? "emptyFieldMessage" : ""}" placeholder={$t("Enter your feedback here")} />
        {#if showEmptyFeedbackMessage}
          <p id="empty-feedback-message">{$t("Please enter your feedback")}</p>
        {/if}
      </div>
      <div class="button-wrapper">
        <button class="submit-button" on:click={handleSubmit}>{$t("Submit")}</button>
      </div>
    </div>
    {/if}
  </div>
</div>

<style lang="scss">

  .overlay {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(68, 68, 68, 0.5);
    z-index: 999; 
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .drawer {
    position: fixed;
    bottom: 0;
    right: 0;
    width: 350px;
    height: wrap;
    max-height: 60%;
    background: white;
    box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
    transform: translateX(100%);
    transition: transform 0.3s ease;
    z-index: 1000;
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    margin-bottom: 2vh;
    border: 1px solid var(--color-gray);
  }

  .drawer.open {
    transform: translateX(0);
    margin-right: 0.5vh;
  }

  .drawer-header {
    padding: 0 16px;
    background: var(--color-primary);
    color: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0; /* Ensure header doesn't shrink */

    h3 {
      margin: 15px 0;
    }
  }

  .drawer-body {
    overflow-y: auto; /* Make it scrollable */
    min-height: 400px;

    #feedback-form{
      flex: 1; /* Take up remaining space */
      display: flex;
      flex-direction: column;
      padding: 16px 16px 30px 16px;
      gap: 16px;
      overflow-y: auto; /* Make it scrollable */
    }

    p {
      color: black;
      margin: 0;
    }

    textarea, input {
      padding: 8px;
      border: 2px solid #ccc;
      border-radius: 5px;
      background: darken(white, 5%);
      transition: border 0.5s, resize 0.1s;
      outline: none;
      box-sizing: border-box;
    }

    textarea:focus, input[type=text]:focus {
      border: 2px solid #555;
    }

    .feedback-field-wrapper {
      position: relative;
      display: flex;
      flex-direction: column;
    }

    #input-feedback {
      min-height: 100px;
      resize: vertical;

      &.emptyFieldMessage {
        border: 2px solid var(--color-tertiary);
      }
    }

    #empty-feedback-message {
      color: var(--color-tertiary);
      margin: 0;
    }

    .result-screen {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 16px;
      padding: 16px;
      text-align: center;
      min-height: 350px;

      .result-screeen-icon {
      display: flex;
      justify-content: center;
      color: black;
      width: 80px;
      height: 80px;

        &.success {
          color: var(--color-primary);
        }

        &.failure {
          color: red;
        }
      }

      p {
        margin: 0;
        font-size: 20px;
      }
    }
  }

  .close-button {
    background: none;
    border: none;
    color: white;
    font-size: 20px;
    cursor: pointer;
    height: 20px;
    width: 20px;
  }

  .button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .submit-button {
    background: var(--color-primary);
    color: white;
    border: none;
    padding: 8px 16px;
    margin-top: 16px;
    cursor: pointer;
    width: 100px;
    border-radius: 5px;
    margin: 0;
  }

  /* Mobile styles */
  @media (max-width: 768px) {

    .overlay {
      display: block;
      pointer-events: none;
    }

    .overlay.visible {
      display: block;
      opacity: 1;
      pointer-events: auto; /* Enable interaction when visible */
    }
    .drawer {
      position: fixed;
      bottom: 1%;
      left: 2.5%;
      transform: translateX(110%);
      width: 95vw;
      height: 60vh;
    }

    .drawer.open {
      transform: translateX(0);
    }

    .drawer-header {
      padding: 0 12px;

      h3 {
        margin: 12px 0;
      }
    }

    .drawer-body {
      gap: 12px;
    }

    .submit-button {
      padding: 8px 12px;
    }
  }
</style>
